import { useMessages } from '@hooks/useMessages';
import {
  hasMinPasswordLength,
  hasPasswordNumberOrSymbols,
  hasPasswordUpperLowerCase,
} from '@src/@data/utility/password';
import React, { useMemo } from 'react';

interface PasswordHelperProps {
  password: string;
}

enum PasswordStrength {
  weak,
  average,
  good,
  strong,
}

const getPasswordStrength = (password: string): PasswordStrength => {
  let strength = 0;
  if (!hasMinPasswordLength(password)) {
    strength = 0;
  } else {
    if (hasMinPasswordLength(password)) {
      strength += 1;
    }
    if (password.length >= 10) {
      strength += 1;
    }
    if (hasPasswordUpperLowerCase(password)) {
      strength += 1;
    }
    if (hasPasswordNumberOrSymbols(password)) {
      strength += 1;
    }
  }

  if (strength >= 3) {
    return PasswordStrength.strong;
  }
  return strength as PasswordStrength;
};

const passwordStrengthWidthMap: Record<PasswordStrength, number> = {
  [PasswordStrength.weak]: 25,
  [PasswordStrength.average]: 50,
  [PasswordStrength.good]: 75,
  [PasswordStrength.strong]: 100,
};

const passwordStrengthColorMap: Record<PasswordStrength, string> = {
  [PasswordStrength.weak]: '#FF597D',
  [PasswordStrength.average]: '#F9AD76',
  [PasswordStrength.good]: '#7180b7',
  [PasswordStrength.strong]: '#77C1C6',
};

export const PasswordHelper: React.FC<PasswordHelperProps> = ({ password }) => {
  const { messages } = useMessages();

  const strength = getPasswordStrength(password);

  const passwordStrengthTextMap: Record<PasswordStrength, 'string'> = useMemo(
    () => ({
      [PasswordStrength.weak]: messages('password_strength_weak'),
      [PasswordStrength.average]: messages('password_strength_average'),
      [PasswordStrength.good]: messages('password_strength_good'),
      [PasswordStrength.strong]: messages('password_strength_strong'),
    }),
    [messages]
  );

  return (
    <>
      <div
        className="rounded"
        style={{
          height: '4px',
          width: `${passwordStrengthWidthMap[strength]}%`,
          backgroundColor: passwordStrengthColorMap[strength],
        }}
      />
      <p className="mt-50">
        <small>{passwordStrengthTextMap[strength]}</small>
      </p>
    </>
  );
};
